import { useContainerBlockContext, useCustomViewBlockContext } from '@lighthouse/block'
import type { CustomViewConfig, ViewBlockAbstract } from '@lighthouse/core'
import {
    collectViewFieldIdsFromFilter,
    findRecordInPool,
    getAppointField,
    getReadyCustomStatisticFields,
    getWithScopeId,
    getWithScopeIdAndRecordIdAndColumnId,
    normalizeUniqueIdValue,
    useAtomAction
} from '@lighthouse/shared'
import { useAtomValue, useStore } from 'jotai'
import { useEffect, useMemo } from 'react'

import { mergeRecordStatisticAtom } from '@/atoms/dataSource/action'
import { recordPoolAtom, viewIndependentDataAtomFamily } from '@/atoms/dataSource/state'
import { useCurrentPageContext, useCurrentStackIdContext } from '@/contexts/PageContext'
import { useCurrentAppId, useCurrentEnvId } from '@/hooks/useApplication'
import { useDataSource } from '@/hooks/useDataSource'
import * as srv from '@/services'

interface UseCustomViewStatisticPayload {
    blockData: ViewBlockAbstract
}

export const useCustomViewStatistic = ({ blockData }: UseCustomViewStatisticPayload) => {
    const { id, config } = blockData
    const { pointer, customStatistic } = config as CustomViewConfig
    const fields = customStatistic?.fields
    const readyFields = useMemo(() => getReadyCustomStatisticFields(fields), [fields])

    const appId = useCurrentAppId()
    const envId = useCurrentEnvId()
    const { pageId } = useCurrentPageContext()
    const stackId = useCurrentStackIdContext()
    const { scope } = useContainerBlockContext()
    const { record } = useCustomViewBlockContext()
    const uniqueId = getWithScopeIdAndRecordIdAndColumnId(id, scope, record?.id)
    const scopedViewId = getWithScopeId(uniqueId, scope)
    const dataSource = useDataSource(appId, envId, pointer)
    const store = useStore()
    const { run: mergeRecordStatistic } = useAtomAction(mergeRecordStatisticAtom)

    const uniqueIdField = customStatistic?.uniqueIdField || (dataSource ? getAppointField(dataSource, 'ID')?.id : undefined) || 'ID'
    const viewFieldIds = useMemo(
        () => [...new Set(readyFields.flatMap(field => collectViewFieldIdsFromFilter(field.filter)))],
        [readyFields]
    )

    const viewIndependentData = useAtomValue(
        viewIndependentDataAtomFamily({
            stackId,
            dsId: pointer,
            viewId: scopedViewId
        })
    )
    const recordPoints = viewIndependentData.recordPoints
    const recordIdsKey = useMemo(() => recordPoints.map(item => item.id).join(','), [recordPoints])
    const readyFieldsKey = useMemo(() => JSON.stringify(readyFields), [readyFields])

    useEffect(() => {
        if (!pointer || readyFields.length === 0 || !recordIdsKey) {
            return
        }
        const recordIds = recordIdsKey.split(',')
        const pool = store.get(recordPoolAtom)
        const uniqueIdValues: unknown[] = []
        const viewRows: { uniqueIdValue: unknown; values: Record<string, unknown> }[] = []
        const seen = new Set<string>()
        for (const recordId of recordIds) {
            const current = findRecordInPool(pool, pointer, recordId)
            const raw = current?.content?.[uniqueIdField]?.value
            const key = normalizeUniqueIdValue(raw)
            if (seen.has(key)) {
                continue
            }
            seen.add(key)
            uniqueIdValues.push(raw ?? '')
            const values: Record<string, unknown> = {}
            for (const fieldId of viewFieldIds) {
                values[fieldId] = current?.content?.[fieldId]?.value
            }
            viewRows.push({ uniqueIdValue: raw ?? '', values })
        }
        if (uniqueIdValues.length === 0) {
            return
        }

        const abort = new AbortController()
        void srv
            .getViewCustomStatistic({
                appId,
                envId,
                pageId,
                viewId: id,
                uniqueIdField,
                uniqueIdValues,
                viewRows,
                fields: readyFields,
                abort
            })
            .then(result => {
                if (abort.signal.aborted || !result?.items?.length) {
                    return
                }
                mergeRecordStatistic({
                    stackId,
                    dsId: pointer,
                    viewId: scopedViewId,
                    recordIds,
                    uniqueIdField,
                    items: result.items
                })
            })
            .catch(() => undefined)

        return () => {
            abort.abort()
        }
    }, [
        appId,
        envId,
        id,
        mergeRecordStatistic,
        pageId,
        pointer,
        readyFields,
        readyFieldsKey,
        recordIdsKey,
        scopedViewId,
        stackId,
        store,
        uniqueIdField,
        viewFieldIds
    ])

    return viewIndependentData.customStatistic
}
